// VCF (vCard) インポート用ユーティリティ。
// vCard 2.1 / 3.0 / 4.0 を緩やかに受け入れる軽量パーサ。
//
// 想定エクスポート元:
// - iOS 連絡先 (vCard 3.0、UTF-8、ADR の TYPE=WORK 等)
// - Google Contacts (vCard 3.0、UTF-8、X-PHONETIC-* あり)
// - LINE myBridge (vCard 3.0)
// - Outlook (vCard 2.1、QUOTED-PRINTABLE 含むことあり)
//
// 仕様の細部 (RFC 6350) よりも実用性優先:
// 文字化け / フォーマット欠損があっても可能な限り取り込む。

import type { BusinessCard } from "./types";

// ============================================================
// 公開型
// ============================================================

export interface ParsedVCF {
  cards: BusinessCard[];
  errors: string[];
  /** 元の VCARD ブロック数 (パース成功有無に関わらず) */
  totalBlocks: number;
}

// ============================================================
// ファイル → テキスト
// ============================================================

/**
 * 行折り返し (RFC 6350 §3.2: CRLF + SP/HT) を **バイトレベル** で除去。
 * これを decode 前に行わないと UTF-8 のマルチバイト文字が
 * 75 オクテット境界で切られている場合に置換文字 (U+FFFD) になる。
 */
function unfoldBytes(buf: ArrayBuffer): Uint8Array {
  const src = new Uint8Array(buf);
  const out = new Uint8Array(src.length);
  let oi = 0;
  let i = 0;
  while (i < src.length) {
    const b = src[i];
    // CR LF SP/HT を 3 バイトまとめて削除
    if (
      b === 0x0d &&
      i + 2 < src.length &&
      src[i + 1] === 0x0a &&
      (src[i + 2] === 0x20 || src[i + 2] === 0x09)
    ) {
      i += 3;
      continue;
    }
    // LF SP/HT を 2 バイトまとめて削除
    if (
      b === 0x0a &&
      i + 1 < src.length &&
      (src[i + 1] === 0x20 || src[i + 1] === 0x09)
    ) {
      i += 2;
      continue;
    }
    out[oi++] = b;
    i++;
  }
  return out.slice(0, oi);
}

/**
 * バイトレベル unfold → UTF-8 / Shift-JIS の順で decode → BOM 除去。
 */
async function readVcfText(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const unfolded = unfoldBytes(buffer);
  let text = new TextDecoder("utf-8", { fatal: false }).decode(unfolded);
  if (text.includes("�")) {
    try {
      text = new TextDecoder("shift_jis", { fatal: false }).decode(unfolded);
    } catch {
      // ignore
    }
  }
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  return text;
}

// ============================================================
// 論理行への分割 + QUOTED-PRINTABLE soft break 結合
// ============================================================

/**
 * デコード済みテキストを論理行の配列に変換。
 * vCard 2.1 の QUOTED-PRINTABLE soft break (`=\n`) を結合する。
 * (折り返しはすでに `unfoldBytes` で処理済み)
 */
function splitLogicalLines(text: string): string[] {
  const physical = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const out: string[] = [];
  for (let i = 0; i < physical.length; i++) {
    let line = physical[i];
    // QP soft break: 行末が `=` で、その行が ENCODING=QUOTED-PRINTABLE を宣言している場合、
    // 後続行を結合 (継続行の先頭がインデントされていなくても OK)
    if (/ENCODING\s*=\s*QUOTED-PRINTABLE/i.test(line)) {
      while (line.endsWith("=") && i + 1 < physical.length) {
        i++;
        line = line.slice(0, -1) + physical[i];
      }
    }
    out.push(line);
  }
  return out;
}

// ============================================================
// プロパティ行のパース
// ============================================================

interface VCardProperty {
  name: string; // 大文字化済み
  params: Record<string, string[]>; // キー大文字、値も大文字化
  rawValue: string; // unescape 前
}

/**
 * パラメータ部 (`;` 区切り) でも `;` 自身がクォート内にあるケースを
 * 許容しつつ、`:` の出現位置 (= 値の開始) を探す。
 */
function findValueDelimiter(line: string): number {
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      inQuote = !inQuote;
    } else if (c === ":" && !inQuote) {
      return i;
    }
  }
  return -1;
}

function parseProperty(line: string): VCardProperty | null {
  const colonIdx = findValueDelimiter(line);
  if (colonIdx < 0) return null;

  const meta = line.slice(0, colonIdx);
  const rawValue = line.slice(colonIdx + 1);

  // meta を `;` で分割 (param value 内のクォート対応)
  const segments: string[] = [];
  let buf = "";
  let inQuote = false;
  for (let i = 0; i < meta.length; i++) {
    const c = meta[i];
    if (c === '"') {
      inQuote = !inQuote;
      buf += c;
    } else if (c === ";" && !inQuote) {
      segments.push(buf);
      buf = "";
    } else {
      buf += c;
    }
  }
  segments.push(buf);

  const name = segments[0]?.trim().toUpperCase();
  if (!name) return null;

  // ITEM1.TEL, ITEM2.EMAIL のような prefix を取り除く (iOS の typical export)
  const cleanName = name.replace(/^ITEM\d+\./, "");

  const params: Record<string, string[]> = {};
  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i];
    if (!seg) continue;
    const eqIdx = seg.indexOf("=");
    if (eqIdx < 0) {
      // bare value (vCard 2.1 の TEL;WORK:... 形式)
      const upper = seg.trim().toUpperCase();
      if (!params.TYPE) params.TYPE = [];
      params.TYPE.push(upper);
    } else {
      const key = seg.slice(0, eqIdx).trim().toUpperCase();
      let val = seg.slice(eqIdx + 1).trim();
      if (val.startsWith('"') && val.endsWith('"')) {
        val = val.slice(1, -1);
      }
      const splitVals = val
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      if (!params[key]) params[key] = [];
      params[key].push(...splitVals);
    }
  }

  return { name: cleanName, params, rawValue };
}

// ============================================================
// 値のデコード
// ============================================================

/**
 * QUOTED-PRINTABLE デコード (vCard 2.1 + 日本語の典型)。
 * `=XX` の連続を指定 charset のバイト列として解釈。
 */
function decodeQuotedPrintable(input: string, charset: string): string {
  const normalized = input.replace(/=\r?\n/g, ""); // soft break (念のため)
  const bytes: number[] = [];
  let i = 0;
  while (i < normalized.length) {
    const c = normalized[i];
    if (c === "=" && i + 2 < normalized.length) {
      const hex = normalized.slice(i + 1, i + 3);
      if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
        bytes.push(parseInt(hex, 16));
        i += 3;
        continue;
      }
    }
    bytes.push(normalized.charCodeAt(i) & 0xff);
    i++;
  }
  try {
    return new TextDecoder(charset.toLowerCase(), { fatal: false }).decode(
      new Uint8Array(bytes),
    );
  } catch {
    return new TextDecoder("utf-8", { fatal: false }).decode(
      new Uint8Array(bytes),
    );
  }
}

/** vCard テキストエスケープを実体化 (\n / \, / \; / \\) */
function unescapeText(v: string): string {
  return v
    .replace(/\\N/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/\\\\/g, "\\");
}

/**
 * セミコロン区切りの構造化値を **エスケープを尊重して** 分割。
 * `\;` はリテラルセミコロンとして 1 部品内に保たれる。
 * 分割後の各部品は `unescapeText` で実体化する。
 */
function splitStructured(value: string): string[] {
  const parts: string[] = [];
  let buf = "";
  let escaped = false;
  for (let i = 0; i < value.length; i++) {
    const c = value[i];
    if (escaped) {
      buf += "\\" + c;
      escaped = false;
    } else if (c === "\\") {
      escaped = true;
    } else if (c === ";") {
      parts.push(buf);
      buf = "";
    } else {
      buf += c;
    }
  }
  if (escaped) buf += "\\"; // 不正な末尾エスケープでもデータは捨てない
  parts.push(buf);
  return parts.map((p) => unescapeText(p));
}

/**
 * プロパティの値を最終的な表示文字列に変換 (非構造化値向け)。
 * BASE64 エンコードや PHOTO は空文字を返してメモ等への混入を防ぐ。
 */
function decodeValue(p: VCardProperty): string {
  const enc = p.params.ENCODING?.[0];
  const charset = p.params.CHARSET?.[0] ?? "UTF-8";
  let v = p.rawValue;
  if (enc === "QUOTED-PRINTABLE") {
    v = decodeQuotedPrintable(v, charset);
  } else if (enc === "BASE64" || enc === "B") {
    return ""; // PHOTO 等。Sheets には入れない
  }
  return unescapeText(v);
}

/** プロパティが構造化値 (N / ORG / ADR) のとき、エスケープ尊重で分割した配列を返す */
function decodeStructured(p: VCardProperty): string[] {
  const enc = p.params.ENCODING?.[0];
  const charset = p.params.CHARSET?.[0] ?? "UTF-8";
  let v = p.rawValue;
  if (enc === "QUOTED-PRINTABLE") {
    v = decodeQuotedPrintable(v, charset);
  } else if (enc === "BASE64" || enc === "B") {
    return [];
  }
  return splitStructured(v);
}

// ============================================================
// VCARD ブロック → BusinessCard
// ============================================================

const CJK_REGEX = /[　-ヿ一-鿿＀-￯]/;

/**
 * iOS / Google から書き出される TEL の TYPE は色々ある:
 * CELL, MOBILE, IPHONE → mobile
 * FAX → fax
 * それ以外 (WORK / HOME / VOICE / MAIN / etc) → phone
 */
function classifyTel(types: string[]): "fax" | "mobile" | "phone" {
  const upper = types.map((t) => t.toUpperCase());
  if (upper.includes("FAX")) return "fax";
  if (
    upper.includes("CELL") ||
    upper.includes("MOBILE") ||
    upper.includes("IPHONE")
  ) {
    return "mobile";
  }
  return "phone";
}

/**
 * vCard 4.0 で `TEL:tel:+81-90-...;ext=123` の形を正規化。
 * - `tel:` スキームを除去
 * - URI パラメータ (`;ext=...`) を除去
 * - URL デコード
 */
function normalizeTel(value: string): string {
  let s = value.trim();
  if (/^tel:/i.test(s)) s = s.slice(4);
  const semi = s.indexOf(";");
  if (semi >= 0) s = s.slice(0, semi);
  try {
    s = decodeURIComponent(s);
  } catch {
    // ignore malformed
  }
  return s.trim();
}

/** 住所配列を表示文字列に組み立て (CJK 含むなら無区切り、それ以外は ", " 区切り) */
function joinAddress(parts: string[]): string {
  const cleaned = parts.filter((p) => p && p.trim() !== "");
  if (cleaned.length === 0) return "";
  const isCjk = cleaned.some((p) => CJK_REGEX.test(p));
  return cleaned.join(isCjk ? "" : ", ");
}

/**
 * iOS の vCard エクスポートは region/locality/street に同じ都道府県名を
 * 重複させることがある (`region="東京都"`, `street="東京都千代田区..."`)。
 * 隣接する CJK パートで前者が後者の prefix なら前者を捨てる。
 */
function dedupeCjkAddressParts(parts: string[]): string[] {
  const cleaned = parts.map((p) => p.trim()).filter(Boolean);
  return cleaned.filter((part, i) => {
    const next = cleaned[i + 1];
    return !(next && CJK_REGEX.test(part + next) && next.startsWith(part));
  });
}

function blockToCard(props: VCardProperty[]): BusinessCard {
  const card: BusinessCard = { name: "" };

  // TEL は分類が必要なので一旦集めてから振り分け
  const tels: Array<{ value: string; cls: "fax" | "mobile" | "phone" }> = [];
  const overflowMemo: string[] = [];

  for (const p of props) {
    switch (p.name) {
      case "FN": {
        const v = decodeValue(p);
        if (v && !card.name) card.name = v.trim();
        break;
      }

      case "N": {
        if (!card.name) {
          // Family;Given;Additional;Prefix;Suffix
          const parts = decodeStructured(p);
          const family = (parts[0] ?? "").trim();
          const given = (parts[1] ?? "").trim();
          const isCjk = CJK_REGEX.test(family + given);
          const joined = isCjk
            ? `${family}${given ? " " + given : ""}`
            : `${given}${family ? " " + family : ""}`;
          card.name = joined.trim();
        }
        break;
      }

      case "ORG": {
        // Company;Department[;sub-dept...]
        const parts = decodeStructured(p);
        if (!card.company && parts[0]) card.company = parts[0].trim();
        if (!card.department && parts[1]) {
          card.department = parts.slice(1).join(" / ").trim();
        }
        break;
      }

      case "TITLE":
      case "ROLE": {
        const v = decodeValue(p);
        if (v && !card.title) card.title = v.trim();
        break;
      }

      case "TEL": {
        const v = decodeValue(p);
        if (!v) break;
        tels.push({
          value: normalizeTel(v),
          cls: classifyTel(p.params.TYPE ?? []),
        });
        break;
      }

      case "EMAIL": {
        const v = decodeValue(p);
        if (!v) break;
        const trimmed = v.trim();
        if (!card.email1) card.email1 = trimmed;
        else if (!card.email2 && trimmed !== card.email1) card.email2 = trimmed;
        break;
      }

      case "URL": {
        const v = decodeValue(p);
        if (v && !card.url) card.url = v.trim();
        break;
      }

      case "ADR": {
        // PO Box;Extended;Street;Locality;Region;Postcode;Country
        const parts = decodeStructured(p).map((s) => s.trim());
        const poBox = parts[0] ?? "";
        const extended = parts[1] ?? "";
        const street = parts[2] ?? "";
        const locality = parts[3] ?? "";
        const region = parts[4] ?? "";
        const postcode = parts[5] ?? "";
        const country = parts[6] ?? "";

        if (!card.postalCode && postcode) {
          card.postalCode = postcode;
        }
        if (!card.address) {
          // CJK と Western で語順を切り替え。POBox / extended も保持して情報損失を防ぐ
          const allFields = [country, region, locality, street, extended, poBox];
          const isCjk = allFields.some((f) => CJK_REGEX.test(f));
          const ordered = isCjk
            ? [country, region, locality, street, extended, poBox]
            : [poBox, extended, street, locality, region, country];
          card.address = joinAddress(
            isCjk ? dedupeCjkAddressParts(ordered) : ordered,
          );
        }
        break;
      }

      case "NOTE": {
        const v = decodeValue(p);
        if (v.trim()) {
          card.memo = card.memo ? `${card.memo}\n${v.trim()}` : v.trim();
        }
        break;
      }

      case "BDAY": {
        const v = decodeValue(p);
        if (v.trim()) overflowMemo.push(`誕生日: ${v.trim()}`);
        break;
      }

      case "X-PHONETIC-FIRST-NAME":
      case "X-PHONETIC-LAST-NAME":
      case "X-PHONETIC-ORG": {
        const v = decodeValue(p);
        if (v.trim()) {
          overflowMemo.push(
            `${p.name.replace(/^X-PHONETIC-/, "ふりがな ")}: ${v.trim()}`,
          );
        }
        break;
      }

      case "CATEGORIES": {
        const v = decodeValue(p);
        const tags = v
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (tags.length > 0) {
          const existing = card.tags ?? [];
          card.tags = [...new Set([...existing, ...tags])];
        }
        break;
      }

      case "BEGIN":
      case "END":
      case "VERSION":
      case "PHOTO":
      case "REV":
      case "UID":
      case "PRODID":
      case "X-ABUID":
      case "X-ABLABEL":
      case "X-ABADR":
      case "X-SOCIALPROFILE":
      case "X-IMPP":
      case "IMPP":
        break;

      default: {
        // 未知の標準プロパティはメモに退避 (ベンダー独自 X-* は捨てる)
        if (p.name.startsWith("X-")) break;
        const v = decodeValue(p);
        if (v.trim()) overflowMemo.push(`${p.name}: ${v.trim()}`);
        break;
      }
    }
  }

  // TEL 振り分け: fax > mobile > phone の優先で埋める
  for (const t of tels) {
    if (t.cls === "fax" && !card.fax) {
      card.fax = t.value;
    } else if (t.cls === "mobile" && !card.mobile) {
      card.mobile = t.value;
    } else if (t.cls === "phone" && !card.phone) {
      card.phone = t.value;
    } else if (!card.mobile) {
      card.mobile = t.value;
    } else if (!card.phone) {
      card.phone = t.value;
    } else {
      overflowMemo.push(
        `電話 (${t.cls === "fax" ? "FAX" : t.cls === "mobile" ? "携帯" : "その他"}): ${t.value}`,
      );
    }
  }

  if (overflowMemo.length > 0) {
    const extra = overflowMemo.join("\n");
    card.memo = card.memo ? `${card.memo}\n${extra}` : extra;
  }

  return card;
}

// ============================================================
// メイン
// ============================================================

/**
 * VCF ファイル全体をパースして BusinessCard 配列を返す。
 * 不正な VCARD ブロックがあっても残りはパース継続する。
 */
export async function parseVcfFile(file: File): Promise<ParsedVCF> {
  const text = await readVcfText(file);
  const lines = splitLogicalLines(text);

  const cards: BusinessCard[] = [];
  const errors: string[] = [];
  let totalBlocks = 0;

  let inBlock = false;
  let current: VCardProperty[] = [];

  const finalize = () => {
    try {
      const card = blockToCard(current);
      if (card.name || card.company || card.email1 || card.phone || card.mobile) {
        cards.push(card);
      } else {
        errors.push(
          `block #${totalBlocks}: 識別できる名前・連絡先がありません (スキップ)`,
        );
      }
    } catch (e) {
      errors.push(
        `block #${totalBlocks}: ${e instanceof Error ? e.message : "不明なエラー"}`,
      );
    }
  };

  for (let idx = 0; idx < lines.length; idx++) {
    const line = lines[idx];
    if (!line.trim()) continue;

    const upper = line.trim().toUpperCase();
    if (upper === "BEGIN:VCARD") {
      // 入れ子: 直前のブロックが END で閉じていない場合は警告して破棄
      if (inBlock) {
        errors.push(
          `block #${totalBlocks}: END:VCARD が無いまま新しい BEGIN:VCARD が出現 (前のブロックを破棄)`,
        );
      }
      inBlock = true;
      current = [];
      totalBlocks++;
      continue;
    }
    if (upper === "END:VCARD") {
      if (inBlock) {
        finalize();
      }
      inBlock = false;
      current = [];
      continue;
    }
    if (!inBlock) continue;

    const prop = parseProperty(line);
    if (prop) current.push(prop);
  }

  // 閉じ忘れ
  if (inBlock && current.length > 0) {
    errors.push(`最後のブロックが END:VCARD で閉じていません (取り込まずスキップ)`);
  }

  return { cards, errors, totalBlocks };
}

// ============================================================
// インポートオプション (CSV と共通の振る舞い)
// ============================================================

export interface VcfImportOptions {
  bulkTags?: string[];
  fillTodayIfEmpty?: boolean;
}

/** YYYY-MM-DD で今日を返す (ローカル日付) */
function todayLocalDate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * パース済みカードに一括タグ付与・取得日デフォルトを適用。
 */
export function applyVcfImportOptions(
  cards: BusinessCard[],
  options: VcfImportOptions = {},
): BusinessCard[] {
  const fillToday = options.fillTodayIfEmpty ?? true;
  const today = todayLocalDate();
  return cards.map((c) => {
    const next: BusinessCard = { ...c };
    if (!next.acquiredAt && fillToday) {
      next.acquiredAt = today;
    }
    if (options.bulkTags && options.bulkTags.length > 0) {
      const existing = next.tags ?? [];
      next.tags = [...new Set([...existing, ...options.bulkTags])];
    }
    return next;
  });
}

/** ファイルが VCF として扱えるかの簡易判定 (ドラッグ&ドロップ時の防御) */
export function isVcfFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (name.endsWith(".vcf") || name.endsWith(".vcard")) return true;
  if (file.type && /vcard|vcf|directory/i.test(file.type)) return true;
  return false;
}
